import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, switchMap, filter, take, from } from 'rxjs';
import { environment } from '../../environments/environment';
import { getConfig } from '../config.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';

export interface IvTokenResponse {
  token: string;
  expiresAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavvisTokenService {
  private http = inject(HttpClient);
  private oidcSecurityService = inject(OidcSecurityService);

  /**
   * Gets NavVis IVION token.
   * In dev mode: calls NavVis API directly with dev credentials.
   * In prod mode: uses IdToken from OIDC (same as what middleware returns).
   */
  getIvionToken(): Observable<string> {
    // Dev mode: call NavVis API directly
    if (environment.skipAuth) {
      console.log('[DEV MODE] Getting NavVis token directly');
      return from(this.getDevToken());
    }

    // Prod mode: use IdToken from OIDC (middleware just returns this anyway)
    return this.oidcSecurityService.isAuthenticated$.pipe(
      filter((authResult) => authResult.isAuthenticated),
      take(1),
      switchMap(() =>
        this.oidcSecurityService.getIdToken().pipe(
          map((idToken) => {
            if (!idToken) {
              throw new Error('No IdToken available');
            }
            console.log('[NavvisTokenService] Using IdToken for IVION');
            return idToken;
          }),
          catchError((error) => {
            console.error('Failed to get IdToken:', error);
            throw error;
          })
        )
      )
    );
  }

  /**
   * Gets NavVis token directly from NavVis API (dev mode only).
   */
  private async getDevToken(): Promise<string> {
    const creds = (environment as any).devNavvisCredentials;
    if (!creds) {
      throw new Error('Dev credentials not configured');
    }

    const response = await fetch(`${getConfig().api.navvisUrl}/api/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: creds.username,
        password: creds.password,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get NavVis token: ${response.status}`);
    }

    const data = await response.json();
    return data.token;
  }

  /**
   * Gets NavVis token as a Promise for use in non-Angular code.
   */
  async getIvionTokenAsync(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.getIvionToken().subscribe({
        next: (token) => resolve(token),
        error: (err) => reject(err),
      });
    });
  }
}
